/**
 * Qualified event listener — exposed as ThryvQualified.Events.
 *
 * Listens for qualified() handleEvents callbacks (Meeting Booked,
 * Conversation Started, Workflow Meeting Booked, Email Captured) and
 * fans them out as the `qualified.*` DOM CustomEvents that other modules
 * (e.g. src2/DS3/Events.js) subscribe to.
 *
 * Backend sends (ESB, Tray) live in src2/DS3/Events.js. See
 * vault/TODOs/qualified-events-* for the de-dupe refactor and the
 * ContentSquare/Tealium removal that simplified this module.
 *
 * Pure utility functions live in Helpers.js (ThryvQualified.Helpers).
 *
 * Debug: ?thryvDebug=qualifiedEvents (via helpers.shouldDebug)
 */
import Helpers from "./Helpers.js";

const Events = new Object();

// ---------------------------------------------------------------------------
// Module-private helpers
// ---------------------------------------------------------------------------

/** Debug logger gated by ?thryvDebug=qualifiedEvents. */
const log = function (...args) {
  if (typeof helpers === "undefined" || !helpers.shouldDebug("qualifiedEvents")) return;
  console.log("[ThryvQualified.Events]", ...args);
};

// ---------------------------------------------------------------------------
// Public methods
// ---------------------------------------------------------------------------

/** Bootstrap event listening. Idempotent. */
Events.init = function () {
  if (this.__initialized) return;
  this.__initialized = true;

  Helpers.waitForSdk(() => this.fireEvents());
  log("initialized");
};

/**
 * Subscribe to qualified() handleEvents and dispatch matching DOM events.
 *
 * Standard documented events from Qualified University:
 *   - 'Meeting Booked'
 *   - 'Conversation Started'
 *   - 'Email Captured'
 *
 * Plus account-specific events emitted by Qualified Workflows configured
 * for Thryv (verified in admin → Settings → Workflows):
 *   - 'Workflow Meeting Booked'
 */
Events.fireEvents = function () {
  const self = this;

  window.qualified("handleEvents", function (name, data) {
    log("SDK callback:", name, data);

    if (name === "Meeting Booked") {
      self.dispatchEvent("qualified.meeting-booked", data);
    } else if (name === "Conversation Started") {
      self.dispatchEvent("qualified.conversation-started", data);
    } else if (name === "Email Captured") {
      self.dispatchEvent("qualified.email-captured", data);
    } else if (name === "Workflow Meeting Booked") {
      self.dispatchEvent("qualified.workflow-meeting-booked", data);
      self.sendToESB(data);
    }
  });
};

/** Dispatch a CustomEvent on document. */
Events.dispatchEvent = function (eventName, detail) {
  log("Dispatched DOM event:", eventName, detail);
  document.dispatchEvent(new CustomEvent(eventName, { detail }));
};

/** Send data to ESB */
var isSending = false;
Events.sendToESB = function (data) {
  if (!isSending) {
    if (helpers.shouldDebug("qualifiedEvents"))
      console.log("[ThryvQualified.sendToESB] sending data:", data);
    isSending = true;

    if (!data || typeof data !== "object" || !data.field_values) {
      console.warn("[ThryvQualified.sendToESB] invalid data passed to send:", data);
      return;
    }

    // Send qualified appointment data to ESB/TIBCO
    const esbPayload = Helpers.buildEsbPayload(data);
    const esbEndpoint =
      thryvSettings.env === "staging"
        ? "https://cfit-esb.thryv.com/enterprise/eventlistener"
        : "https://esb.thryv.com/enterprise/eventlistener";

    if (helpers.shouldDebug("qualifiedEvents"))
      console.log("[ThryvQualified.buildEsbPayload] esbPayload:", esbPayload);
    $.ajax({
      type: "POST",
      url: thryvData.ajax_url + "?action=send_qualified_data&endpoint=" + esbEndpoint,
      data: esbPayload,
      success: function (response) {
        if (response.data.response.code == 200) {
          if (helpers.shouldDebug("qualifiedEvents"))
            console.log("[ThryvQualified.sendToESB] ESB Response:", response.data.body);
        } else {
          console.error("[ThryvQualfied.sendToESB] ESB Status:", response.data.response.code);
          console.error("[ThryvQualfied.sendToESB] ESB Error:", response.data.response.message);
        }
      },
      error: function (jqXHR, textStatus, errorThrown) {
        console.error("[ThryvQualfied.sendToESB] ESB Status:", textStatus);
        console.error("[ThryvQualfied.sendToESB] ESB Error:", errorThrown);
        console.error("[ThryvQualfied.sendToESB] ESB Response Body:", jqXHR.responseText);
      },
    });

    // Send qualified appointment data to Tray
    const endpoint = "https://dc84776b-002f-48f8-9618-710a44446d65.trayapp.io";
    $.ajax({
      type: "POST",
      url: thryvData.ajax_url + "?action=post_to_tray",
      data: { submissionUrl: window.location.href, esbPayload, endpoint },
      success: function (response) {
        if (helpers.shouldDebug("qualifiedEvents"))
          console.log("[ThryvQualfied.sendToESB] Tray Response:", response);
      },
      error: function (response) {
        console.error("[ThryvQualfied.sendToESB] Tray Error:", response);
      },
    });
  }
};

export default Events;
